import React, { useRef, useEffect } from 'react';
import { 
  Bold, Italic, Underline, List, AlignLeft, AlignCenter,
  Type, Image as ImageIcon, Loader2 
} from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * NATIVE RICH TEXT EDITOR COMPONENT
 * No external dependencies like react-quill required.
 */
const NativeRichEditor = ({ value, onChange, onImageUpload, isUploading, className }) => {
  const savedRange = useRef(null);
  const editorRef = useRef(null);

  // Sync value to editor if it changes externally
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const execCommand = (command, val = null) => {
    restoreSelection();
    document.execCommand(command, false, val);
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      // Only save if the selection is inside our editor
      if (editorRef.current && editorRef.current.contains(range.commonAncestorContainer)) {
        savedRange.current = range;
      }
    }
  };

  const restoreSelection = () => {
    const sel = window.getSelection();
    if (savedRange.current) {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
      editorRef.current?.focus();
    } else if (editorRef.current) {
      editorRef.current.focus();
      // Move cursor to end if no selection
      const range = document.createRange();
      range.selectNodeContents(editorRef.current);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  };

  const handleImageClick = () => {
    saveSelection();
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        const url = await onImageUpload(file);
        if (url) {
          restoreSelection();
          // Small delay to ensure focus and selection are stable
          setTimeout(() => {
            document.execCommand('insertImage', false, url);
            handleInput();
          }, 10);
        }
      }
    };
    input.click();
  };

  const handleColorChange = (e) => {
    execCommand('foreColor', e.target.value);
  };

  return (
    <div className={cn("flex flex-col h-full bg-background border border-border rounded-xl overflow-hidden shadow-sm", className)}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-1 bg-muted/20 border-b border-border shadow-sm">
        <button type="button" onClick={() => execCommand('bold')} className="p-1.5 hover:bg-primary/10 hover:text-primary rounded-md transition-colors" title="Bold"><Bold className="w-3.5 h-3.5" /></button>
        <button type="button" onClick={() => execCommand('italic')} className="p-1.5 hover:bg-primary/10 hover:text-primary rounded-md transition-colors" title="Italic"><Italic className="w-3.5 h-3.5" /></button>
        <button type="button" onClick={() => execCommand('underline')} className="p-1.5 hover:bg-primary/10 hover:text-primary rounded-md transition-colors" title="Underline"><Underline className="w-3.5 h-3.5" /></button>
        <div className="w-px h-3.5 bg-border mx-1" />
        <button type="button" onClick={() => execCommand('insertUnorderedList')} className="p-1.5 hover:bg-primary/10 hover:text-primary rounded-md transition-colors" title="List"><List className="w-3.5 h-3.5" /></button>
        <button type="button" onClick={() => execCommand('justifyLeft')} className="p-1.5 hover:bg-primary/10 hover:text-primary rounded-md transition-colors" title="Align Left"><AlignLeft className="w-3.5 h-3.5" /></button>
        <button type="button" onClick={() => execCommand('justifyCenter')} className="p-1.5 hover:bg-primary/10 hover:text-primary rounded-md transition-colors" title="Align Center"><AlignCenter className="w-3.5 h-3.5" /></button>
        <div className="w-px h-3.5 bg-border mx-1" />
        <div className="relative flex items-center group p-1.5 hover:bg-primary/10 rounded-md">
          <Type className="w-3.5 h-3.5 mr-1 text-muted-foreground" />
          <input type="color" onChange={handleColorChange} className="w-3.5 h-3.5 cursor-pointer bg-transparent border-none p-0 overflow-hidden" title="Text Color" />
        </div>
        <button type="button" onClick={handleImageClick} className="p-1.5 hover:bg-primary/10 hover:text-primary rounded-md transition-colors" title="Upload Image"><ImageIcon className="w-3.5 h-3.5" /></button>
      </div>

      {/* Editable Area */}
      <div className="relative flex-1 flex flex-col min-h-0">
        <div
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          onBlur={saveSelection}
          onKeyUp={saveSelection}
          onMouseUp={saveSelection}
          className="flex-1 p-4 overflow-y-auto focus:outline-none prose prose-sm prose-zinc max-w-none text-foreground leading-relaxed"
          style={{ minHeight: '150px' }}
        />
        {isUploading && (
          <div className="absolute inset-0 bg-background/40 backdrop-blur-[2px] flex items-center justify-center z-10">
            <div className="bg-card p-3 rounded-xl border border-border shadow-lg flex items-center gap-2">
              <Loader2 className="w-4 h-4 text-primary animate-spin" />
              <span className="text-[10px] font-black uppercase tracking-widest text-primary">Uploading...</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NativeRichEditor;
